import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  TEACHINGS_LIST_QUERY_KEY,
  TEACHINGS_QUERY_KEY,
  createTeaching,
  createTeachingDetailQueryKey,
  createTeachingFileQueryKey,
  deleteTeaching,
  fetchTeachingById,
  fetchTeachingDocumentFile,
  fetchTeachings,
  fetchTeachingsForInjunction,
  fetchTeachingsForImposition,
  fetchTeachingsForReporting,
  type CreateTeachingRequest,
} from "@/api/teachings";
import { invalidateTeachingQueries } from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  TeachingEvidenceListModel,
  TeachingEvidenceModel,
  TeachingQueryParams,
} from "@/features/cle-compliance/models/teachingDocument";

export {
  TEACHINGS_LIST_QUERY_KEY,
  TEACHINGS_QUERY_KEY,
  createTeachingDetailQueryKey,
  createTeachingFileQueryKey,
} from "@/api/teachings";

export type { CreateTeachingRequest } from "@/api/teachings";

export const useTeachings = (params: TeachingQueryParams = {}) =>
  useQuery<PaginatedResponse<TeachingEvidenceListModel>>({
    queryKey: [...TEACHINGS_LIST_QUERY_KEY, params],
    queryFn: () => fetchTeachings(params),
    staleTime: 5 * 60 * 1000,
  });

export const useTeachingsForReporting = (reportId: string) =>
  useQuery<TeachingEvidenceListModel[]>({
    queryKey: [...TEACHINGS_QUERY_KEY, "for-reporting", reportId],
    queryFn: () => fetchTeachingsForReporting(reportId),
    staleTime: 5 * 60 * 1000,
  });

export const useTeachingsForInjunction = (
  injunctionId: string,
  enabled = true,
) =>
  useQuery<TeachingEvidenceListModel[]>({
    enabled: enabled && injunctionId.length > 0,
    queryKey: [...TEACHINGS_QUERY_KEY, "for-injunction", injunctionId],
    queryFn: () => fetchTeachingsForInjunction(injunctionId),
    staleTime: 5 * 60 * 1000,
  });

export const useTeachingsForImposition = (
  impositionId: string,
  enabled = true,
) =>
  useQuery<TeachingEvidenceListModel[]>({
    enabled: enabled && impositionId.length > 0,
    queryKey: [...TEACHINGS_QUERY_KEY, "for-imposition", impositionId],
    queryFn: () => fetchTeachingsForImposition(impositionId),
    staleTime: 5 * 60 * 1000,
  });

export const useTeachingDocument = (id: string) =>
  useQuery<TeachingEvidenceModel>({
    enabled: id.length > 0,
    queryKey: createTeachingDetailQueryKey(id),
    queryFn: () => fetchTeachingById(id),
    staleTime: 5 * 60 * 1000,
  });

export const useTeachingDocumentFile = (id: string) =>
  useQuery<Blob>({
    enabled: id.length > 0,
    queryKey: createTeachingFileQueryKey(id),
    queryFn: () => fetchTeachingDocumentFile(id),
    staleTime: 5 * 60 * 1000,
  });

export const useCreateTeaching = () => {
  const queryClient = useQueryClient();

  return useMutation<TeachingEvidenceModel, Error, CreateTeachingRequest>({
    mutationFn: createTeaching,
    onSuccess: () => {
      invalidateTeachingQueries(queryClient);
    },
  });
};

export const useDeleteTeaching = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteTeaching,
    onSuccess: (_data, id) => {
      invalidateTeachingQueries(queryClient, id);
    },
  });
};
